import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress, Grid, IconButton, Button } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { fetchAllCustomers, ICustomer, deleteCustomer } from '../../../core/API/customers';
import './CustomerTable.scss';

interface Customer extends ICustomer {
	id: number;
}

interface CustomerTableProps {
	title?: string;
}

const CustomerTable: React.FC<CustomerTableProps> = ({ title = 'Customer' }) => {
	const history = useHistory();
	const [customers, setCustomers] = useState<Customer[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		fetchCustomers();
	}, []);

	const fetchCustomers = async () => {
		setLoading(true);
		try {
			const data = await fetchAllCustomers();
			// Handle both array and { member: [...] } formats
			const customerList = Array.isArray(data) ? data : (data?.member || []);
			setCustomers(customerList.map((item: any) => ({
				id: item.id,
				firstName: item.firstName || '',
				lastName: item.lastName || '',
				fullName: item.fullName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : ''),
				email: item.email || '',
				phone: item.phone || null,
				companyName: item.companyName || null,
				address: item.address || null,
				title: item.title || null,
				notes: item.notes || null,
				workplace: item.workplace || undefined,
			})));
		} catch (error) {
			console.error('Error fetching customers:', error);
			setCustomers([]);
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id: number) => {
		if (window.confirm('Are you sure you want to delete this customer?')) {
			try {
				await deleteCustomer(id);
				setCustomers(customers.filter(c => c.id !== id));
			} catch (error) {
				console.error('Error deleting customer:', error);
			}
		}
	};

	const handleEdit = (customer: Customer) => {
		history.push({
			pathname: `/manager/customers/edit/${customer.id}`,
			state: { customerData: customer }
		});
	};

	const handleAdd = () => {
		history.push('/manager/customers/create');
	};

	return (
		<Grid container direction="column" className="customerTableCard customer-table-container white-card">
			<Grid item className="customer-table-header">
				<h2>{title}</h2>
				<button onClick={handleAdd} className="add-button">
					Add
				</button>
			</Grid>
			{loading ? (
				<Grid item className="loading-container">
					<CircularProgress size={20} />
				</Grid>
			) : (
				<Grid item className="customer-table-content scrollable">
					<table className="customer-table">
						<thead>
							<tr>
								<th>Customer Name</th>
								<th>Email</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{customers.map((customer) => {
								const displayName = (customer as any).fullName || (customer.firstName && customer.lastName ? `${customer.firstName} ${customer.lastName}` : '-');
								return (
								<tr key={customer.id}>
									<td>{displayName}</td>
									<td>{customer.email || '-'}</td>
									<td>
										<IconButton onClick={() => handleEdit(customer)} size="small" className="edit-btn">
											<Edit fontSize="small" />
										</IconButton>
										<IconButton onClick={() => handleDelete(customer.id!)} size="small" className="delete-btn">
											<Delete fontSize="small" />
										</IconButton>
									</td>
								</tr>
							)})}
							{customers.length === 0 && !loading && (
								<tr>
									<td colSpan={3} className="no-customers">
										No customers found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</Grid>
			)}
		</Grid>
	);
};

export default CustomerTable;

